/**
 * Quote editor — `/sales/quotes/new` and `/sales/quotes/$quoteId`.
 *
 * One screen for writing a quote and for what happens to it afterwards,
 * because they are the same document at different moments.
 *
 * Four facts it states rather than hides.
 *
 * The totals are the server's. Nothing here adds up a line for display: the
 * command computes every amount from what it was sent, and the screen shows
 * what came back. A total computed twice is a total that can disagree.
 *
 * Each line's tax is determined by the same engine an invoice uses, so what a
 * customer is quoted is what they are billed — and the determined rate is
 * shown per line, not just the amount.
 *
 * Editing stops at acceptance. What a customer accepted is what they accepted,
 * so an accepted, declined, expired or converted quote is read-only here and
 * the form says which, rather than failing on save.
 *
 * Converting is idempotent. A quote already converted answers with the order
 * it became, and the screen reports that rather than claiming a new one.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
  Textarea,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { formatCalendarDate } from "@/lib/format";
import { useTaxCodeList } from "@/features/finance/queries";
import { usePartyOptions } from "@/features/finance/queries";
import { useVariantOptions } from "@/features/catalog/queries";

import {
  QUOTE_TRANSITIONS,
  convertQuote,
  createQuote,
  isQuoteEditable,
  replaceQuote,
  transitionQuote,
  type Quote,
  type QuoteStatus,
  type QuoteWrite,
} from "../quotes-api";
import { salesKeys, useQuote } from "../queries";

interface LineDraft {
  variant_id: string | null;
  description: string;
  qty: string;
  unit_price: string;
  discount_amount: string;
  tax_code_id: string | null;
}

const blankLine = (): LineDraft => ({
  variant_id: null,
  description: "",
  qty: "1",
  unit_price: "0",
  discount_amount: "0",
  tax_code_id: null,
});

export interface QuoteEditorPageProps {
  quoteId?: string;
  defaultCurrency?: string;
  canWrite?: boolean;
  canConvert?: boolean;
  onSaved: (quoteId: string) => void;
  onOpenOrder: (salesOrderId: string) => void;
  today?: string;
}

export function QuoteEditorPage({
  quoteId,
  defaultCurrency = "SAR",
  canWrite = false,
  canConvert = false,
  onSaved,
  onOpenOrder,
  today = new Date().toISOString().slice(0, 10),
}: QuoteEditorPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const existing = useQuote(quoteId);
  const quote: Quote | undefined = existing.data;

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [quoteDate, setQuoteDate] = useState(today);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const customers = usePartyOptions("customer");
  const variants = useVariantOptions("");
  const taxCodes = useTaxCodeList({ active: true }, 200);

  useEffect(() => {
    if (quote === undefined) return;
    setCustomerId(quote.quote.customer_id);
    setCurrency(quote.quote.currency);
    setQuoteDate(quote.quote.quote_date);
    setValidUntil(quote.quote.valid_until);
    setNotes(quote.quote.notes);
    setLines(
      quote.lines.map((line) => ({
        variant_id: line.variant_id,
        description: line.description,
        qty: line.qty,
        unit_price: line.unit_price,
        discount_amount: line.discount_amount,
        tax_code_id: line.tax_code_id ?? null,
      })),
    );
  }, [quote]);

  const status: QuoteStatus = quote?.quote.status ?? "draft";
  const editable = quote === undefined || isQuoteEditable(status);
  const nextStates = QUOTE_TRANSITIONS[status];

  const variantOptions = useMemo(
    () => (variants.data ?? []).map((variant) => ({ value: variant.id, label: variant.sku })),
    [variants.data],
  );
  const customerOptions = useMemo(
    () => (customers.data ?? []).map((party) => ({ value: party.id, label: party.display_name })),
    [customers.data],
  );
  const taxOptions = useMemo(
    () => taxCodes.rows.map((code) => ({ value: code.id, label: `${code.code} · ${code.name}` })),
    [taxCodes.rows],
  );

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const complete =
    customerId !== null &&
    validUntil !== "" &&
    validUntil >= quoteDate &&
    lines.length > 0 &&
    lines.every((line) => line.variant_id !== null && line.qty.trim() !== "");

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  const save = () => {
    if (!complete) return;
    setBusy(true);
    setError(null);
    const body: QuoteWrite = {
      customer_id: customerId as string,
      currency,
      quote_date: quoteDate,
      valid_until: validUntil,
      ...(notes.trim() === "" ? {} : { notes: notes.trim() }),
      lines: lines.map((line, index) => ({
        line_no: index + 1,
        variant_id: line.variant_id as string,
        qty: line.qty.trim(),
        unit_price: line.unit_price.trim() === "" ? "0" : line.unit_price.trim(),
        ...(line.description.trim() === "" ? {} : { description: line.description.trim() }),
        ...(line.discount_amount.trim() === "" || line.discount_amount.trim() === "0"
          ? {}
          : { discount_amount: line.discount_amount.trim() }),
        ...(line.tax_code_id === null ? {} : { tax_code_id: line.tax_code_id }),
      })),
    };
    const request =
      quoteId === undefined ? createQuote(body, newIdempotencyKey()) : replaceQuote(quoteId, body);
    void request
      .then(async (saved) => {
        await queryClient.invalidateQueries({ queryKey: salesKeys.quotes.all });
        onSaved(saved.quote.id);
      })
      .catch(() => setError(t("sales.quoteEditor.saveError", "The quote could not be saved.")))
      .finally(() => setBusy(false));
  };

  const move = (to: QuoteStatus) => {
    if (quoteId === undefined) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    // Re-issuing a lapsed quote needs a validity that has not already passed;
    // sending it again with the old one would send something already dead.
    const reissue = status === "expired" && to === "sent";
    void transitionQuote(quoteId, to, reissue ? validUntil : undefined, newIdempotencyKey())
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: salesKeys.quotes.all });
      })
      .catch(() => setError(t("sales.quoteEditor.moveError", "That move was refused.")))
      .finally(() => setBusy(false));
  };

  const convert = () => {
    if (quoteId === undefined) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    void convertQuote(quoteId, newIdempotencyKey())
      .then(async (conversion) => {
        setOutcome(
          conversion.created
            ? t("sales.quoteEditor.converted", "This quote is now a draft sales order.")
            : t(
                "sales.quoteEditor.alreadyConverted",
                "This quote had already become an order; nothing new was written.",
              ),
        );
        await queryClient.invalidateQueries({ queryKey: salesKeys.quotes.all });
      })
      .catch(() =>
        setError(t("sales.quoteEditor.convertError", "The quote could not be converted.")),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {quote === undefined
            ? t("sales.quoteEditor.newTitle", "New quote")
            : quote.quote.quote_number}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {quote === undefined ? (
            t(
              "sales.quoteEditor.newSubtitle",
              "A proposal: nothing moves and nothing posts until it becomes an order.",
            )
          ) : (
            <>
              <Badge variant={status === "converted" ? "success" : "secondary"}>
                {t(`sales.quoteStatus.${status}`, status)}
              </Badge>
              <span>
                {t("sales.quoteEditor.validUntil", "Valid until {{date}}", {
                  date: day(quote.quote.valid_until),
                })}
              </span>
            </>
          )}
        </p>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {outcome === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {outcome}
        </p>
      )}

      {editable ? null : (
        <p className="rounded-md border p-3 text-sm text-muted-foreground">
          {t(
            "sales.quoteEditor.frozen",
            "This quote is no longer editable. What a customer accepted is what they accepted.",
          )}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.quoteEditor.terms", "Terms")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="quote-customer">{t("sales.quoteEditor.customer", "Customer")}</Label>
            <Combobox
              id="quote-customer"
              options={customerOptions}
              value={customerId}
              disabled={!editable || !canWrite}
              onChange={setCustomerId}
              placeholder={t("sales.quoteEditor.pickCustomer", "Pick a customer…")}
              searchPlaceholder={t("actions.search", "Search")}
              emptyMessage={t("sales.quoteEditor.noCustomers", "No customer matches.")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quote-currency">{t("sales.quoteEditor.currency", "Currency")}</Label>
            <Input
              id="quote-currency"
              dir="ltr"
              maxLength={3}
              className="w-24 font-mono uppercase"
              value={currency}
              disabled={quote !== undefined || !canWrite}
              onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            />
            <p className="text-xs text-muted-foreground">
              {t("sales.quoteEditor.currencyFixed", "Fixed once the quote exists.")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quote-date">{t("sales.quoteEditor.quoteDate", "Quote date")}</Label>
            <Input
              id="quote-date"
              type="date"
              dir="ltr"
              value={quoteDate}
              disabled={!editable || !canWrite}
              onChange={(event) => setQuoteDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quote-valid-until">
              {t("sales.quoteEditor.validUntilLabel", "Valid until")}
            </Label>
            <Input
              id="quote-valid-until"
              type="date"
              dir="ltr"
              value={validUntil}
              disabled={(!editable && status !== "expired") || !canWrite}
              onChange={(event) => setValidUntil(event.target.value)}
            />
            {validUntil !== "" && validUntil < quoteDate ? (
              <p className="text-sm text-destructive">
                {t(
                  "sales.quoteEditor.badValidity",
                  "A quote that expires before it is written is not a quote.",
                )}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="quote-notes">{t("sales.quoteEditor.notes", "Notes")}</Label>
            <Textarea
              id="quote-notes"
              rows={2}
              value={notes}
              disabled={!editable || !canWrite}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("sales.quoteEditor.lines", "Lines")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lines.map((line, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[2fr_2fr_1fr_1fr_1fr_2fr_auto]">
              <Combobox
                options={variantOptions}
                value={line.variant_id}
                disabled={!editable || !canWrite}
                onChange={(value) => setLine(index, { variant_id: value })}
                placeholder={t("sales.quoteEditor.pickVariant", "SKU…")}
                searchPlaceholder={t("actions.search", "Search")}
                emptyMessage={t("sales.quoteEditor.noVariants", "No SKU matches.")}
                aria-label={t("sales.quoteEditor.sku", "SKU")}
              />
              <Input
                aria-label={t("sales.quoteEditor.description", "Description")}
                value={line.description}
                disabled={!editable || !canWrite}
                onChange={(event) => setLine(index, { description: event.target.value })}
              />
              <Input
                dir="ltr"
                inputMode="decimal"
                className="text-end font-mono"
                aria-label={t("sales.quoteEditor.qty", "Quantity")}
                value={line.qty}
                disabled={!editable || !canWrite}
                onChange={(event) => setLine(index, { qty: event.target.value })}
              />
              <Input
                dir="ltr"
                inputMode="decimal"
                className="text-end font-mono"
                aria-label={t("sales.quoteEditor.unitPrice", "Unit price")}
                value={line.unit_price}
                disabled={!editable || !canWrite}
                onChange={(event) => setLine(index, { unit_price: event.target.value })}
              />
              <Input
                dir="ltr"
                inputMode="decimal"
                className="text-end font-mono"
                aria-label={t("sales.quoteEditor.discount", "Discount")}
                value={line.discount_amount}
                disabled={!editable || !canWrite}
                onChange={(event) => setLine(index, { discount_amount: event.target.value })}
              />
              <Combobox
                options={taxOptions}
                value={line.tax_code_id}
                disabled={!editable || !canWrite}
                onChange={(value) => setLine(index, { tax_code_id: value })}
                placeholder={t("sales.quoteEditor.noTax", "No tax")}
                searchPlaceholder={t("actions.search", "Search")}
                emptyMessage={t("sales.quoteEditor.noTaxCodes", "No tax code matches.")}
                aria-label={t("sales.quoteEditor.taxCode", "Tax code")}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={!editable || !canWrite || lines.length <= 1}
                aria-label={t("sales.quoteEditor.removeLine", "Remove line")}
                onClick={() => setLines(lines.filter((_, at) => at !== index))}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </div>
          ))}

          {editable && canWrite ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLines([...lines, blankLine()])}
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("sales.quoteEditor.addLine", "Add line")}
            </Button>
          ) : null}

          {quote === undefined ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "sales.quoteEditor.totalsPending",
                "Totals and tax are computed by the server when this is saved — the same determination an invoice will use.",
              )}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[36rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("sales.quoteEditor.line", "Line")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("sales.quoteEditor.net", "Net")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("sales.quoteEditor.taxRate", "Rate")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("sales.quoteEditor.tax", "Tax")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("sales.quoteEditor.lineTotal", "Total")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {quote.lines.map((line) => (
                    <tr key={line.id} className="border-b last:border-0">
                      <td className="p-2">
                        {line.description === "" ? line.line_no : line.description}
                      </td>
                      <td className="p-2 text-end">
                        <MoneyDisplay
                          value={{ amount: line.net_amount, currency: quote.quote.currency }}
                          display="none"
                        />
                      </td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">
                        {line.tax_percent}%
                      </td>
                      <td className="p-2 text-end">
                        <MoneyDisplay
                          value={{ amount: line.tax_amount, currency: quote.quote.currency }}
                          display="none"
                        />
                      </td>
                      <td className="p-2 text-end">
                        <MoneyDisplay
                          value={{ amount: line.line_total, currency: quote.quote.currency }}
                          display="none"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="font-medium">
                    <td className="p-2">{t("sales.quoteEditor.total", "Quote total")}</td>
                    <td className="p-2 text-end">
                      <MoneyDisplay
                        value={{
                          amount: quote.quote.subtotal,
                          currency: quote.quote.currency,
                        }}
                        display="none"
                      />
                    </td>
                    <td className="p-2" />
                    <td className="p-2 text-end">
                      <MoneyDisplay
                        value={{ amount: quote.quote.tax_total, currency: quote.quote.currency }}
                        display="none"
                      />
                    </td>
                    <td className="p-2 text-end">
                      <MoneyDisplay
                        value={{
                          amount: quote.quote.grand_total,
                          currency: quote.quote.currency,
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        {editable && canWrite ? (
          <Button type="button" disabled={!complete || busy} onClick={save}>
            {quoteId === undefined
              ? t("sales.quoteEditor.create", "Save the quote")
              : t("actions.save", "Save")}
          </Button>
        ) : null}

        {quoteId === undefined || !canWrite
          ? null
          : nextStates.map((next) => (
              <Button
                key={next}
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => move(next)}
              >
                {t(`sales.quoteMove.${next}`, next)}
              </Button>
            ))}

        {quote !== undefined && status === "accepted" && canConvert ? (
          <Button type="button" disabled={busy} onClick={convert}>
            <ArrowRight className="size-4" aria-hidden="true" />
            {t("sales.quoteEditor.convert", "Turn into an order")}
          </Button>
        ) : null}

        {quote?.quote.converted_order_id === null ||
        quote?.quote.converted_order_id === undefined ? null : (
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenOrder(quote.quote.converted_order_id as string)}
          >
            {t("sales.quoteEditor.openOrder", "Open the order it became")}
          </Button>
        )}
      </div>
    </div>
  );
}
